import React, { useState, useEffect, useRef, useMemo } from 'react';
import TypingEditor from './TypingEditor';
import { useCourse } from '../../providers/CourseContext';
const ProgrammingEditor = React.lazy(() => import('./ProgrammingEditor'));

export function CourseProgress() {
  const {
    updateProgressTimeSpent,
    courseName,
    progress,
    allQuestions,
    currentQuestionIndex,
    timeSpent,
  } = useCourse();
  const currentSection = useMemo(() => {
    if (!allQuestions) return '';
    const currentQuestion = allQuestions[currentQuestionIndex];
    return currentQuestion?.section || '';
  }, [allQuestions, currentQuestionIndex]);

  const [elapsedTime, setElapsedTime] = useState(timeSpent);
  const lastTrackedMinuteRef = useRef<number>(-1);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Update progress only when elapsed minutes change
  useEffect(() => {
    // Calculate current minute
    const currentMinute = Math.floor(elapsedTime / 60);

    // Only call updateProgressTimeSpent when we enter a new minute and haven't tracked it yet
    if (currentMinute > lastTrackedMinuteRef.current) {
      lastTrackedMinuteRef.current = currentMinute;
      updateProgressTimeSpent(1);
    }
  }, [elapsedTime, updateProgressTimeSpent]);

  // Update the timer every second
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime((prev: number) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setElapsedTime(timeSpent);
    // Reset the lastTrackedMinute when timeSpent is updated from the API
    lastTrackedMinuteRef.current = Math.floor(timeSpent / 60);
  }, [timeSpent]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 py-2 px-2 md:px-4 flex">
      <h2
        className="col-span-1 md:col-span-8 font-source-sans text-lg md:text-xl lg:text-2xl font-bold"
        style={{
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
        }}
      >
        {courseName}{' '}
        {currentSection && (
          <span className="font-normal">- {currentSection.title}</span>
        )}
      </h2>
      <div className="col-span-1 md:col-span-4 flex items-center justify-end gap-4">
        <div className="text-center md:text-right mt-2 md:mt-0">
          {/* <div className="text-sm font-medium">
            Course Duration:{' '}
            <span className="text-gray-800 font-semibold">
              {courseDuration}
            </span>
          </div> */}
          <div className="font-medium text-sm md:text-base mt-1">
            <span className="text-gray-700">Duration:</span>{' '}
            <span className="text-blue-700 font-semibold text-base md:text-lg">
              {formatTime(elapsedTime)}
            </span>
          </div>
        </div>
        <div>
          <div className="bg-gray-300 rounded-full h-6 md:h-[28px] w-48 md:w-[240px] relative overflow-hidden">
            <div
              className="bg-green-400 h-full rounded-full"
              style={{ width: `${progress > 100 ? 100 : progress}%` }}
            />
            <div
              className={`absolute inset-0 flex items-center text-xs font-medium ${
                progress > 50
                  ? 'justify-end pr-2 text-white'
                  : 'justify-start pl-2 text-gray-800'
              }`}
            >
              Course {progress > 100 ? 100 : progress}% Completed
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CodeEditorComponent() {
  // Get context data
  const { language } = useCourse();

  return (
    <>
      <CourseProgress />
      {language === 'typing' ? (
        <TypingEditor />
      ) : (
        <React.Suspense fallback={<div>Loading...</div>}>
          <ProgrammingEditor />
        </React.Suspense>
      )}
    </>
  );
}
